<template>
  <CustomTable ref="table" :elements=elements :data="data" :loadData="loadData">
    <template v-slot:table>
      <thead>
        <tr>
          <custom-header field="name">Groupe</custom-header>
          <custom-header field="userCount">Utilisateurs</custom-header>
          <custom-header field="activeUsers30d" class="hide-for-mobile">Actifs (30 j)</custom-header>
          <custom-header field="connections7d" class="hide-for-mobile">Connexions (7 j)</custom-header>
          <custom-header field="connections30d" class="hide-for-mobile">Connexions (30 j)</custom-header>
          <custom-header field="lastConnectionAt" class="hide-for-mobile">Dernière connexion</custom-header>
          <custom-header field="options" :sortable="false" class="hide-for-mobile">Options</custom-header>
          <custom-header field="actions" :sortable="false">Actions</custom-header>
        </tr>
      </thead>
      <tbody v-if="data && data.length > 0">
        <tr v-for="group in filteredData()" :key="group.id">
          <custom-body field="name">
            <router-link :to="`/administration/groups/${group.id}`" class="group-link">{{ groupLabel(group.name) }}</router-link>
            <span v-if="group.isSystem" class="tag">Technique</span>
            <span v-if="group.state === 'DEACTIVATED'" class="tag warn">Désactivé</span>
          </custom-body>
          <custom-body field="userCount">
            {{ group.userCount }}<span v-if="group.deactivatedUserCount" class="muted"> ({{ group.deactivatedUserCount }} désactivé<template v-if="group.deactivatedUserCount > 1">s</template>)</span>
          </custom-body>
          <custom-body field="activeUsers30d" class="hide-for-mobile">{{ group.activeUsers30d }}</custom-body>
          <custom-body field="connections7d" class="hide-for-mobile">{{ group.connections7d }}</custom-body>
          <custom-body field="connections30d" class="hide-for-mobile">{{ group.connections30d }}</custom-body>
          <custom-body field="lastConnectionAt" class="hide-for-mobile">{{ dateTime(group.lastConnectionAt) }}</custom-body>
          <custom-body field="options" class="hide-for-mobile">
            <span v-for="option in group.options" :key="option" class="chip">{{ optionTitles[option] ?? option }}</span>
            <span v-if="!group.options.length" class="muted">Aucune</span>
          </custom-body>
          <custom-body field="actions" style="min-width: 25px">
            <button class="button-icon-custom" title="Gérer le groupe" @click="$router.push(`/administration/groups/${group.id}`)">
              <span class="fas fa-sliders"></span>
            </button>
          </custom-body>
        </tr>
      </tbody>
      <tbody v-else><tr class="empty-row"><td colspan="8">Aucun groupe</td></tr></tbody>
    </template>
  </CustomTable>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSliders } from "@fortawesome/free-solid-svg-icons";
import CustomTable from "@/components/CustomTable.vue";
import CustomHeader from "@/components/tables/CustomHeader.vue";
import CustomBody from "@/components/tables/CustomBody.vue";
import {ACTIVATION_STATUS_ENUM} from "@/models/activationStatusEnum";
import {OptionController} from "@/controllers/user/OptionController";
import {AdministrationController} from "@/administration/controllers/AdministrationController";
import {dateTime, groupLabel} from "@/administration/utils";
library.add(faSliders);

/**
 * Les groupes (pour le super admin, les groupes sont les utilisateurs) : utilisateurs, connexions et options de chacun.
 */
export default {
  name: 'GroupTable',
  components: {CustomBody, CustomHeader, CustomTable},
  data() {
    return {
      data: [],
      // Titre de chaque option par son nom ; sans titre connu, le nom de l'option est affiché
      optionTitles: {},
      elements: [
        {label: "Groupe", value: "name", type: "text"},
        {label: "Etat", value: "state", type: "select", values: ACTIVATION_STATUS_ENUM},
        {label: "Utilisateurs", value: "userCount", type: "number"},
        {label: "Actifs (30 j)", value: "activeUsers30d", type: "number"},
        {label: "Connexions (7 j)", value: "connections7d", type: "number"},
        {label: "Connexions (30 j)", value: "connections30d", type: "number"},
        {label: "Dernière connexion", value: "lastConnectionAt", type: "date"},
        {label: "Options", value: undefined, type: undefined},
        {label: "Actions", value: undefined, type: undefined},
      ],
    };
  },
  mounted() {
    this.$refs.table.$refs.searchArray.initWithURLParams();
    this.loadData();
    this.loadOptionTitles();
  },
  methods: {
    dateTime, groupLabel,
    filteredData() {
      return this.$refs.table.$refs.searchArray.simpleFilter(this.data);
    },
    async loadData() {
      this.$refs.table?.startLoading();
      const resp = await AdministrationController.getGroups(this.$refs.table.buildURLParams());
      this.data = !resp.errors ? resp : [];
      this.$refs.table?.stopLoading();
    },
    async loadOptionTitles() {
      const resp = await OptionController.getAll();
      if (!resp.errors)
        this.optionTitles = Object.fromEntries(resp.map(option => [option.name, option.title]));
    },
  },
};
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.group-link
  color THEME_COLOR_PRIMARY
  font-weight 600
  text-decoration none

  &:hover
    text-decoration underline

.muted
  color CTRL_TEXT_MUTED
  font-size 12.5px

.tag
  margin-left 8px
  padding 1px 8px
  font-size 11.5px
  font-weight 600
  color CTRL_TEXT_MUTED
  background CTRL_BG
  border 1px solid CTRL_BORDER
  border-radius 20px

  // Under the name of the group on a small screen, instead of overflowing on the next column
  @media only screen and (max-width: TABLET_SIZE)
    display block
    width fit-content
    margin 3px 0 0

  &.warn
    color #8a5a00
    background #fff6e0
    border-color #f0d9a3

.chip
  display inline-block
  margin 2px 4px 2px 0
  padding 1px 9px
  font-size 12px
  font-weight 600
  color THEME_COLOR_PRIMARY
  background rgba(THEME_COLOR_PRIMARY, 0.08)
  border-radius 20px
</style>
